import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { word } from "@/modules/core/utils/i18n"
import type { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import { ProgramType } from "@/modules/programs/types/programTypes"
import { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import { useProgramSessionActions } from "@/modules/sessions/hooks/useProgramSessionActions"
import {
  getProgramInfiniteSessionValidationSchema,
  type ProgramInfiniteSessionFormikType,
} from "@/modules/sessions/programSessionFormikType"
import { useCreateSessionTemplate } from "@/modules/template/hooks/useTemplate"
import { useFormik } from "formik"
import { useCallback, useState } from "react"

export const useProgramInfiniteOrProSessionFormik = ({
  program,
  editedSession,
  dateSession,
}: {
  program: UserProgramDetailsEntity
  editedSession?: ProgramInfiniteSessionEntity
  dateSession?: string
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const { createSession, updateSession } = useProgramSessionActions()
  const { mutateAsync: createSessionTemplate } = useCreateSessionTemplate()

  const getInitialValues = useCallback(() => {
    if (editedSession) {
      return ProgramInfiniteSessionEntity.fromEntityToFormik(editedSession)
    } else {
      return ProgramInfiniteSessionEntity.getDraftFormik(program.id, program.defaultHideSessionContent, dateSession)
    }
  }, [editedSession, program.id, program.defaultHideSessionContent, dateSession])

  const sessionFormSubmitHandler = async (sessionFormikValues: ProgramInfiniteSessionFormikType) => {
    if (editedSession) {
      return await updateSession({
        id: editedSession.id,
        programId: editedSession.programId,
        programType: ProgramType.VIRTUAL,
        session: ProgramInfiniteSessionEntity.fromFormikToUpdateInputDto(sessionFormikValues),
      })
    } else {
      return await createSession({
        programId: program.id,
        programType: ProgramType.VIRTUAL,
        session: ProgramInfiniteSessionEntity.fromFormikToInputDto(sessionFormikValues),
      })
    }
  }

  const formik = useFormik<ProgramInfiniteSessionFormikType>({
    initialValues: getInitialValues(),
    validationSchema: getProgramInfiniteSessionValidationSchema,
    onSubmit: async () => {
      setIsSubmitting(true)
      try {
        const result = await sessionFormSubmitHandler(formik.values)
        if (formik.values.isTemplateSaved && result) {
          await createSessionTemplate({
            name: formik.values.name,
            description: null,
            templateCategoryId: null,
            sessionId: result.id,
          })
        }
        setIsSubmitting(false)
        toast.success(editedSession ? word("program.session.update.success") : word("program.session.create.success"), {
          classNames: successToastClassName(),
        })
        return true
      } catch (e) {
        setIsSubmitting(false)
        console.error("Session form submit error", e)
        toast.error(word("global.error.default"), {
          classNames: errorToastClassName(),
        })
        return false
      }
    },
  })

  return { formik, isSubmitting }
}
